import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiClock,
  FiMessageCircle,
  FiTrash2,
  FiTrendingUp,
  FiUserPlus,
  FiUsers,
  FiCheckCircle,
  FiXCircle,
  FiPhone,
  FiX,
} from "react-icons/fi";
import csrOutreachService from "../../api/services/csrOutreachService";
import csrLeadsService from "../../api/services/csrLeadsService";
import { getSupportSocket, getAdminId } from "../../api/services/supportSocket";
import CsrCustomerChat from "./CsrCustomerChat";
import CsrCallHistoryModal from "./CsrCallHistoryModal";
import CsrPremiumProofModal from "./CsrPremiumProofModal";
import { layoutCsrChatDock, upsertOpenCsrChat } from "./csrChatDock";
import { toDatetimeLocalValue } from "../../utils/callDuration";
import { CallRecordsTable } from "../../components/CallRecordsTable";
import ConfirmWarningModal from "../../components/ConfirmWarningModal";
import { notifyDailyTargetsChanged } from "../../utils/dailyTargetsEvents";
import {
  clearUserUnreadLocal,
  seedUserUnreadMap,
  subscribeAssignedUnread,
} from "../../utils/assignedChatUnreadStore";
import { TableSkeleton } from "../../components/common/skeletons";
import PaginationBar from "../../components/common/PaginationBar";
import useClientPagination from "../../hooks/useClientPagination";

const THEME = { primary: "#FB565F" };
const inputCls =
  "border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-400 outline-none";

const ALLOWED = ["Admin", "Developer", "CSR", "CSR Team Lead"];
const LIST_PAGE_SIZE = 20;

const EMPTY_FILTERS = {
  unread: "",
  online: "",
  premium: "",
  stage: "",
  buy_premium: "",
  messaged: "",
  agent_id: "",
  is_active: "",
  perfActivity: "",
  callSource: "",
};

function buildCustomerListParams({ search, page, filters, tab }) {
  const params = {
    search: search || undefined,
    page,
    limit: LIST_PAGE_SIZE,
  };
  if (filters.online !== "") params.online = filters.online;
  if (filters.premium !== "") params.premium = filters.premium;
  if (tab === "assign") {
    if (filters.is_active !== "") params.is_active = filters.is_active;
    return params;
  }
  if (filters.unread !== "") params.unread = filters.unread;
  if (filters.stage) params.stage = filters.stage;
  if (filters.buy_premium !== "") params.buy_premium = filters.buy_premium;
  if (filters.messaged !== "") params.messaged = filters.messaged;
  if (tab === "team" && filters.agent_id) params.agent_id = filters.agent_id;
  return params;
}

function haystackIncludes(hay, q) {
  if (!q) return true;
  return String(hay || "").toLowerCase().includes(q);
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <label className="flex flex-col gap-1 min-w-[132px]">
      <span className="text-[11px] font-semibold text-gray-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputCls} w-full`}
      >
        {options.map((o) => (
          <option key={String(o.value)} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function formatAwayDuration(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d`;
  const mo = Math.floor(day / 30);
  return `${mo}mo`;
}

function SessionCell({ customer, showOtherCsrHint, myAgentId }) {
  const status =
    customer.sessionStatus ||
    (customer.isOnline ? "online" : customer.canCall === false ? "logged_out" : "offline");
  const duration = status === "online" ? null : formatAwayDuration(customer.awaySince);
  const cfg =
    status === "online"
      ? { text: "Online", cls: "bg-emerald-100 text-emerald-800" }
      : status === "logged_out"
        ? { text: "Logged out", cls: "bg-amber-100 text-amber-800" }
        : { text: "Offline", cls: "bg-gray-100 text-gray-600" };
  const isOtherCsr =
    showOtherCsrHint &&
    myAgentId &&
    customer.agentId &&
    Number(customer.agentId) !== Number(myAgentId);

  return (
    <div className="min-w-[88px]">
      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold inline-block ${cfg.cls}`}>
        {cfg.text}
      </span>
      {duration && duration !== "—" && (
        <p className="text-[10px] text-gray-500 mt-1 tabular-nums">
          {status === "logged_out" ? "Logged out" : "Offline"} · {duration}
        </p>
      )}
      {isOtherCsr && (
        <p className="text-[10px] text-gray-400 mt-0.5 font-medium">Not in My Users</p>
      )}
    </div>
  );
}

function latestActivityTime(row) {
  const raw = row?.lastMessageAt || row?.updatedAt || row?.assignedAt || 0;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function sortCustomersByLatestMessage(rows) {
  return [...(rows || [])].sort((a, b) => latestActivityTime(b) - latestActivityTime(a));
}

function UnreadBadge({ count, active = false }) {
  const n = Number(count) || 0;
  if (n <= 0) return null;
  return (
    <span
      className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
        active ? "bg-white text-[#FF2D38]" : "bg-[#FF2D38] text-white"
      }`}
    >
      {n > 99 ? "99+" : n}
    </span>
  );
}

function premiumStatusBadge(customer) {
  const status = customer?.premiumRequest?.status;
  if (customer?.buyPremium || status === "approved") {
    return (
      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-emerald-100 text-emerald-800">
        Approved
      </span>
    );
  }
  if (status === "pending") {
    return (
      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-800">
        Pending TL
      </span>
    );
  }
  if (status === "rejected") {
    return (
      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-rose-100 text-rose-800">
        Rejected
      </span>
    );
  }
  return null;
}

function useDebounced(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function TabBtn({ active, onClick, children, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-xl text-sm font-semibold inline-flex items-center gap-2 ${
        active ? "text-white shadow" : "bg-gray-100 text-gray-600"
      }`}
      style={active ? { backgroundColor: THEME.primary } : {}}
    >
      {children}
      <UnreadBadge count={badge} active={active} />
    </button>
  );
}

export default function CsrOutreachPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const userRole = localStorage.getItem("adminUserRole") || "Admin";
  const isAdmin = userRole === "Admin" || userRole === "Developer";
  const isDeveloper = userRole === "Developer";
  const isLead = userRole === "CSR Team Lead";
  const isCsr = userRole === "CSR" || isLead;
  const canManageCsrCustomers = isCsr;

  const [tab, setTab] = useState(isAdmin ? "performance" : "mine");
  const [stages, setStages] = useState([]);
  const [pool, setPool] = useState([]);
  const [mine, setMine] = useState([]);
  const [team, setTeam] = useState([]);
  const [teams, setTeams] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [callRecords, setCallRecords] = useState([]);
  const [quota, setQuota] = useState(null);
  const [period, setPeriod] = useState("daily");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [assigning, setAssigning] = useState(false);
  const [openChats, setOpenChats] = useState([]);
  const expandedChatUserIdsRef = useRef(new Set());
  const openChatUserIds = useMemo(
    () => new Set(openChats.map((x) => Number(x.customer?.userId))),
    [openChats]
  );
  const [callModal, setCallModal] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [loggingCall, setLoggingCall] = useState(false);
  const [deletingCallId, setDeletingCallId] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [dataVersion, setDataVersion] = useState(0);
  const [premiumModalCustomer, setPremiumModalCustomer] = useState(null);
  const [premiumRequests, setPremiumRequests] = useState([]);
  const [approvalModal, setApprovalModal] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [showAddByPhone, setShowAddByPhone] = useState(false);
  const [phoneLead, setPhoneLead] = useState("");
  const [phoneLookup, setPhoneLookup] = useState(null);
  const [phoneLookupError, setPhoneLookupError] = useState("");
  const [attributedModal, setAttributedModal] = useState(null);
  const [attributedUsers, setAttributedUsers] = useState([]);
  const [attributedLoading, setAttributedLoading] = useState(false);
  const [attributedTotal, setAttributedTotal] = useState(0);
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [phoneAssigning, setPhoneAssigning] = useState(false);
  const [teamAgents, setTeamAgents] = useState([]);
  const [reassigningId, setReassigningId] = useState(null);
  const [unassigningId, setUnassigningId] = useState(null);
  const [targetDate, setTargetDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [targetRows, setTargetRows] = useState([]);
  const [targetForm, setTargetForm] = useState({
    agent_id: "",
    target_mode: "calls_and_premium",
    calls_target: "20",
    premium_target: "5",
    apply_to_all: false,
    include_self: false,
  });
  const [savingTargets, setSavingTargets] = useState(false);
  const [listPage, setListPage] = useState(1);
  const [listTotal, setListTotal] = useState(0);
  const [listTotalPages, setListTotalPages] = useState(1);
  const liveSkipLoadingRef = useRef(false);
  const listPageRef = useRef(1);
  const tabRef = useRef(tab);
  tabRef.current = tab;
  listPageRef.current = listPage;
  expandedChatUserIdsRef.current = new Set(
    openChats.filter((x) => !x.minimized).map((x) => Number(x.customer?.userId))
  );

  const filteredTargetRows = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return targetRows;
    return targetRows.filter((r) =>
      haystackIncludes(`${r.agentName || ""} ${r.agentEmail || ""}`, q)
    );
  }, [targetRows, debouncedSearch]);

  const filteredPerformance = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return performance.filter((r) => {
      if (!haystackIncludes(`${r.name || ""} ${r.email || ""}`, q)) return false;
      const calls = Number(r.calls_count) || 0;
      const messages = Number(r.messages_count) || 0;
      if (filters.perfActivity === "has") return calls > 0 || messages > 0;
      if (filters.perfActivity === "none") return calls === 0 && messages === 0;
      return true;
    });
  }, [performance, debouncedSearch, filters.perfActivity]);

  const filteredCallRecords = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return callRecords.filter((c) => {
      if (filters.callSource && c.source !== filters.callSource) return false;
      return haystackIncludes(
        `${c.customerName || ""} ${c.customerEmail || ""} ${c.agentName || ""} ${c.agentEmail || ""}`,
        q
      );
    });
  }, [callRecords, debouncedSearch, filters.callSource]);

  const filteredTeams = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter((t) => {
      if (haystackIncludes(`${t.name || ""} ${t.teamLeadName || ""}`, q)) return true;
      return (t.members || []).some((m) =>
        haystackIncludes(`${m.firstName || ""} ${m.lastName || ""} ${m.email || ""}`, q)
      );
    });
  }, [teams, debouncedSearch]);

  const performancePagination = useClientPagination(filteredPerformance, LIST_PAGE_SIZE);
  const targetRowsPagination = useClientPagination(filteredTargetRows, LIST_PAGE_SIZE);
  const callRecordsPagination = useClientPagination(filteredCallRecords, LIST_PAGE_SIZE);

  const canVerifyPremium = isLead || isAdmin;

  const refresh = useCallback(async () => {
    const silent = liveSkipLoadingRef.current;
    liveSkipLoadingRef.current = false;
    if (!silent) setLoading(true);
    try {
      const stagesRes = await csrOutreachService.getStages();
      setStages(stagesRes.stages || []);

      const applyListPagination = (data) => {
        setListTotal(data?.total ?? 0);
        setListTotalPages(data?.totalPages ?? 1);
      };

      if (tab === "assign" && isCsr) {
        const [poolRes, quotaRes] = await Promise.all([
          csrOutreachService.getPool(
            buildCustomerListParams({
              search: debouncedSearch,
              page: listPage,
              filters,
              tab: "assign",
            })
          ),
          csrOutreachService.getQuota(),
        ]);
        setPool(poolRes.customers || []);
        applyListPagination(poolRes);
        setQuota(quotaRes);
      } else if (tab === "mine" && isCsr) {
        const data = await csrOutreachService.getMine(
          buildCustomerListParams({
            search: debouncedSearch,
            page: listPage,
            filters,
            tab: "mine",
          })
        );
        setMine(sortCustomersByLatestMessage(data.customers || []));
        applyListPagination(data);
        seedUserUnreadMap("csr", data.customers || [], data.totalUnread);
        setQuota(data.quota || null);
      } else if (tab === "team" && (isLead || isAdmin)) {
        const data = await csrOutreachService.getTeam(
          buildCustomerListParams({
            search: debouncedSearch,
            page: listPage,
            filters,
            tab: "team",
          })
        );
        setTeam(sortCustomersByLatestMessage(data.customers || []));
        applyListPagination(data);
      } else if (tab === "targets" && (isLead || isAdmin)) {
        const data = await csrOutreachService.getTargets({ date: targetDate });
        setTargetRows(data.agents || []);
        if (!targetForm.agent_id && data.agents?.length) {
          setTargetForm((f) => ({ ...f, agent_id: String(data.agents[0].agentId) }));
        }
      } else if (tab === "performance") {
        const data = await csrOutreachService.getPerformance({ period });
        setPerformance(data.performance || []);
        setCallRecords(data.callRecords || []);
      } else if (tab === "teams" && (isLead || isAdmin)) {
        const data = await csrOutreachService.getTeams();
        setTeams(data.teams || []);
      }
    } catch {
      if (!silent) toast.error("Failed to load CSR outreach data");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [tab, debouncedSearch, filters, isCsr, isLead, isAdmin, period, targetDate, listPage]);

  const openAttributedClients = useCallback(async (agentRow) => {
    const agentId = Number(agentRow?.agentId);
    if (!agentId) return;
    const now = new Date();
    const today = now.toISOString().slice(0, 10);
    let from = today;
    let to = today;
    if (period === "monthly") {
      const y = now.getUTCFullYear();
      const m = now.getUTCMonth() + 1;
      from = `${y}-${String(m).padStart(2, "0")}-01`;
      const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
      to = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    }
    setAttributedModal({
      agentId,
      agentName: agentRow.name || agentRow.email || `Agent #${agentId}`,
      from,
      to,
    });
    setAttributedLoading(true);
    try {
      const data = await csrLeadsService.listAttributedUsers({
        agent_id: agentId,
        from,
        to,
        limit: 100,
        page: 1,
      });
      setAttributedUsers(data.users || []);
      setAttributedTotal(data.total ?? 0);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load registered clients");
      setAttributedUsers([]);
      setAttributedTotal(0);
    } finally {
      setAttributedLoading(false);
    }
  }, [period]);

  const silentReloadAssignedUsers = useCallback(async () => {
    try {
      const currentFilters = filtersRef.current;
      if (isCsr) {
        const data = await csrOutreachService.getMine(
          buildCustomerListParams({
            search: tabRef.current === "mine" ? debouncedSearch : "",
            page: 1,
            filters: tabRef.current === "mine" ? currentFilters : EMPTY_FILTERS,
            tab: "mine",
          })
        );
        setMine(sortCustomersByLatestMessage(data.customers || []));
        seedUserUnreadMap("csr", data.customers || [], data.totalUnread);
        if (tabRef.current === "mine") {
          setListTotal(data?.total ?? 0);
          setListTotalPages(data?.totalPages ?? 1);
        }
        if (data.quota) setQuota(data.quota);
      }
      if (tabRef.current === "team" && (isLead || isAdmin)) {
        const data = await csrOutreachService.getTeam(
          buildCustomerListParams({
            search: debouncedSearch,
            page: 1,
            filters: currentFilters,
            tab: "team",
          })
        );
        setTeam(sortCustomersByLatestMessage(data.customers || []));
        setListTotal(data?.total ?? 0);
        setListTotalPages(data?.totalPages ?? 1);
      }
    } catch {
      // keep current rows; next full refresh will recover
    }
  }, [debouncedSearch, isCsr, isLead, isAdmin]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    setListPage(1);
    performancePagination.resetPage();
    targetRowsPagination.resetPage();
    callRecordsPagination.resetPage();
  }, [tab, debouncedSearch, period, targetDate, filters]);

  useEffect(() => {
    if (!(isLead || isAdmin)) return;
    csrOutreachService
      .getAccess()
      .then((res) => setTeamAgents(res.access?.agents || []))
      .catch(() => setTeamAgents([]));
  }, [isLead, isAdmin]);

  useEffect(() => {
    const phone = phoneLead.trim();
    if (!showAddByPhone || phone.replace(/\D/g, "").length < 7) {
      setPhoneLookup(null);
      setPhoneLookupError("");
      return;
    }
    let cancelled = false;
    setPhoneChecking(true);
    const t = setTimeout(async () => {
      try {
        const data = await csrOutreachService.lookupPhone(phone);
        if (cancelled) return;
        setPhoneLookup(data);
        setPhoneLookupError("");
      } catch (e) {
        if (cancelled) return;
        setPhoneLookup(null);
        setPhoneLookupError(e?.response?.data?.message || "Phone check failed");
      } finally {
        if (!cancelled) setPhoneChecking(false);
      }
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [phoneLead, showAddByPhone]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [tab, debouncedSearch, filters]);

  // Live unread from shared store (sidebar + sockets)
  useEffect(() => {
    if (!isCsr) return undefined;
    return subscribeAssignedUnread((snap) => {
      const byUser = snap?.csr?.byUserId || {};
      setMine((prev) => {
        let movedToTop = false;
        const next = prev.map((c) => {
          const key = String(c.userId);
          if (!Object.prototype.hasOwnProperty.call(byUser, key)) return c;
          const unread = Number(byUser[key]) || 0;
          const prevUnread = Number(c.unreadCount) || 0;
          if (unread === prevUnread) return c;
          if (unread > prevUnread) movedToTop = true;
          return {
            ...c,
            unreadCount: unread,
            lastMessageAt: unread > prevUnread ? new Date().toISOString() : c.lastMessageAt,
          };
        });
        if (!movedToTop) {
          const unchanged = next.every((c, i) => c === prev[i]);
          return unchanged ? prev : next;
        }
        return sortCustomersByLatestMessage(next);
      });
    });
  }, [isCsr]);

  useEffect(() => {
    if (!isCsr && !isLead && !isAdmin) return undefined;
    const socket = getSupportSocket();
    let timer = null;

    const promoteInList = (setter, userId, chatId, bumpUnread) => {
      setter((prev) => {
        const idx = prev.findIndex(
          (c) =>
            (userId && Number(c.userId) === Number(userId)) ||
            (chatId && Number(c.chatId) === Number(chatId))
        );
        if (idx < 0) return prev;
        const row = {
          ...prev[idx],
          lastMessageAt: new Date().toISOString(),
          unreadCount: bumpUnread
            ? (Number(prev[idx].unreadCount) || 0) + 1
            : prev[idx].unreadCount,
        };
        return [row, ...prev.filter((_, i) => i !== idx)];
      });
    };

    const scheduleLiveReload = (payload = {}) => {
      const userId = payload.userId ?? payload.fromUserId ?? payload.meta?.fromUserId;
      const chatId = payload.chatId ?? payload.meta?.chatId;
      const chatOpen = userId && expandedChatUserIdsRef.current.has(Number(userId));
      const bumpUnread = payload.bumpUnread !== false && !chatOpen;
      if (userId || chatId) {
        promoteInList(setMine, userId, chatId, bumpUnread);
        promoteInList(setTeam, userId, chatId, bumpUnread);
      }
      clearTimeout(timer);
      timer = setTimeout(() => {
        liveSkipLoadingRef.current = true;
        if (
          (tabRef.current === "mine" || tabRef.current === "team") &&
          listPageRef.current !== 1
        ) {
          setListPage(1);
        } else {
          silentReloadAssignedUsers();
        }
      }, 250);
    };

    const onListUpdated = (payload = {}) => {
      if (payload.channel && payload.channel !== "csr") return;
      scheduleLiveReload({ ...payload, bumpUnread: true });
    };

    const onUnread = (payload = {}) => {
      if (payload.channel && payload.channel !== "csr") return;
      if (payload.unreadCount === 0 && !(Number(payload.delta) > 0)) return;
      scheduleLiveReload({
        userId: payload.fromUserId,
        chatId: payload.chatId,
        bumpUnread: Number(payload.delta) > 0,
      });
    };

    const onAlert = (alert = {}) => {
      if (alert.type !== "chat_message") return;
      const channel = alert.kind || alert.meta?.channel;
      if (channel && channel !== "csr") return;
      scheduleLiveReload({
        userId: alert.meta?.fromUserId,
        chatId: alert.meta?.chatId,
        bumpUnread: true,
      });
    };

    const onMessage = (msg = {}) => {
      const adminId = getAdminId();
      const from = Number(msg.user ?? msg.fromUserId ?? msg.senderId);
      if (adminId && Number.isFinite(from) && from === Number(adminId)) return;
      scheduleLiveReload({
        userId: from,
        chatId: msg.chat_id ?? msg.chatId,
        bumpUnread: true,
      });
    };

    socket.on("csrOutreachListUpdated", onListUpdated);
    socket.on("assignedChatUnreadUpdated", onUnread);
    socket.on("adminAlert", onAlert);
    socket.on("message", onMessage);
    return () => {
      clearTimeout(timer);
      socket.off("csrOutreachListUpdated", onListUpdated);
      socket.off("assignedChatUnreadUpdated", onUnread);
      socket.off("adminAlert", onAlert);
      socket.off("message", onMessage);
    };
  }, [isCsr, isLead, isAdmin, silentReloadAssignedUsers]);

  // Deep-link from admin notifications: /csr-outreach?userId=123&chatId=456
  const deepLinkUserId = Number(searchParams.get("userId") || 0) || null;
  const deepLinkHandledRef = useRef(null);
  useEffect(() => {
    if (!deepLinkUserId || loading) return;
    if (deepLinkHandledRef.current === deepLinkUserId) return;
    deepLinkHandledRef.current = deepLinkUserId;
    if (!isAdmin) setTab("mine");
    const fromLists = [...mine, ...team, ...pool].find(
      (c) => Number(c.userId) === Number(deepLinkUserId)
    );
    setOpenChats((prev) =>
      upsertOpenCsrChat(
        prev,
        fromLists || {
          userId: deepLinkUserId,
          firstName: `User #${deepLinkUserId}`,
        }
      )
    );
    const next = new URLSearchParams(searchParams);
    next.delete("userId");
    next.delete("chatId");
    setSearchParams(next, { replace: true });
  }, [deepLinkUserId, loading, mine, team, pool, isAdmin, searchParams, setSearchParams]);

  if (!ALLOWED.includes(userRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  const list =
    tab === "assign" ? pool : tab === "mine" ? mine : tab === "team" ? team : [];

  const myAgentId = getAdminId();

  const updateFilter = (key, value) => {
    setListPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const hasActiveFilters = Boolean(
    search.trim() ||
      filters.unread ||
      filters.online ||
      filters.premium ||
      filters.stage ||
      filters.buy_premium ||
      filters.messaged ||
      filters.agent_id ||
      filters.is_active ||
      filters.perfActivity ||
      filters.callSource
  );

  const searchPlaceholder =
    tab === "teams"
      ? "Search by team or member name..."
      : tab === "targets"
        ? "Search by CSR name or email..."
        : tab === "performance"
          ? "Search CSR, client, or email..."
          : "Search by name, email, or phone...";

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkAssign = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setAssigning(true);
    try {
      const res = await csrOutreachService.assign(ids);
      toast.success(res.message || `Assigned ${ids.length}`);
      if (res.quota) setQuota(res.quota);
      setSelectedIds(new Set());
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Assign failed");
      if (e?.response?.data?.quota) setQuota(e.response.data.quota);
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignByPhone = async () => {
    if (!phoneLead.trim()) {
      toast.error("Phone number is required");
      return;
    }
    if (phoneLookupError) {
      toast.error(phoneLookupError);
      return;
    }
    setPhoneAssigning(true);
    try {
      const res = await csrOutreachService.assignByPhone(phoneLead.trim());
      toast.success(res.message || "Lead assigned");
      if (res.quota) setQuota(res.quota);
      setShowAddByPhone(false);
      setPhoneLead("");
      setPhoneLookup(null);
      setPhoneLookupError("");
      setTab("mine");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Assign by phone failed");
      if (e?.response?.data?.quota) setQuota(e.response.data.quota);
    } finally {
      setPhoneAssigning(false);
    }
  };

  const handleRelease = async (assignmentId) => {
    const reason = window.prompt(
      "Remove from active CRM list. Reason:\n" +
        "inactive | account_deleted | no_longer_active | misuse | logged_out_unavailable | other",
      "inactive"
    );
    if (reason == null) return;
    const notes = window.prompt("Optional notes (or leave blank)", "") || "";
    if (
      !window.confirm(
        "Remove this customer from the active CRM list? They return to the pool and subscription history is kept."
      )
    ) {
      return;
    }
    setUnassigningId(assignmentId);
    try {
      await csrOutreachService.releaseAssignment(assignmentId, {
        reason: String(reason).trim() || "other",
        notes: notes.trim() || undefined,
      });
      toast.success("Customer removed from active CRM list");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Remove failed");
    } finally {
      setUnassigningId(null);
    }
  };

  const handleReassign = async (assignmentId, agentId) => {
    if (!agentId) return;
    setReassigningId(assignmentId);
    try {
      await csrOutreachService.reassignAssignment(assignmentId, Number(agentId));
      toast.success("Customer shifted to another CSR");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Reassign failed");
    } finally {
      setReassigningId(null);
    }
  };

  const handleStage = async (assignmentId, stageId) => {
    if (!canManageCsrCustomers) return;
    try {
      await csrOutreachService.updateStage(assignmentId, stageId);
      toast.success("Status updated");
      refresh();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handlePremium = async (customer, buy) => {
    if (!canManageCsrCustomers || !customer?.assignmentId) return;
    if (buy) {
      if (customer.isPremium) {
        toast("User is already premium — buy premium is not required");
        return;
      }
      if (customer.premiumRequest?.status === "pending") {
        toast.error("Payment proof already pending team lead review");
        return;
      }
      if (customer.buyPremium) {
        toast("Buy premium already approved");
        return;
      }
      setPremiumModalCustomer(customer);
      return;
    }
    if (customer.premiumRequest?.status === "pending") {
      toast.error("Cannot clear while payment proof is pending review");
      return;
    }
    try {
      await csrOutreachService.updateBuyPremium(customer.assignmentId, false);
      toast.success("Cleared buy premium");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update");
    }
  };

  const handlePremiumApproval = async () => {
    if (!approvalModal?.id || !approvalModal?.status) return;
    setApprovingId(approvalModal.id);
    try {
      await csrOutreachService.approvePremiumRequest(approvalModal.id, {
        status: approvalModal.status,
        notes: approvalModal.notes || undefined,
      });
      toast.success(
        approvalModal.status === "approved"
          ? "Payment approved — premium granted"
          : "Payment proof rejected"
      );
      setApprovalModal(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update approval");
    } finally {
      setApprovingId(null);
    }
  };

  const openLogCallModal = (customer) => {
    if (!canManageCsrCustomers) return;
    const ended = new Date();
    const started = new Date(ended.getTime() - 5 * 60 * 1000);
    setCallModal({
      userId: customer.userId,
      email: customer.email || "",
      firstName: customer.firstName,
      lastName: customer.lastName,
      hours: 0,
      minutes: 5,
      seconds: 0,
      started_at: toDatetimeLocalValue(started),
      notes: "",
    });
  };

  const handleLogMinutes = async () => {
    if (!callModal) return;
    const hours = Number(callModal.hours) || 0;
    const minutes = Number(callModal.minutes) || 0;
    const seconds = Number(callModal.seconds) || 0;
    if (hours + minutes + seconds <= 0) {
      toast.error("Enter call duration (hours, minutes, or seconds)");
      return;
    }
    setLoggingCall(true);
    try {
      await csrOutreachService.logCallMinutes({
        user_id: callModal.userId,
        email: callModal.email,
        hours,
        minutes,
        seconds,
        started_at: callModal.started_at ? new Date(callModal.started_at).toISOString() : undefined,
        notes: callModal.notes || "",
      });
      toast.success("Call record saved");
      notifyDailyTargetsChanged({ team: "csr", action: "call_logged" });
      setCallModal(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to log call");
    } finally {
      setLoggingCall(false);
    }
  };

  const executeDeleteCallRecord = async (call) => {
    if (!call?.id || !isDeveloper) return;
    setDeletingCallId(call.id);
    try {
      await csrOutreachService.deleteCallRecord(call.id);
      toast.success("Call record deleted");
      setConfirmModal(null);
      setDataVersion((v) => v + 1);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to delete call record");
    } finally {
      setDeletingCallId(null);
    }
  };

  const requestDeleteCallRecord = (call) => {
    if (!call?.id || !isDeveloper) return;
    setConfirmModal({
      title: "Delete call record?",
      message: "This will permanently remove this call entry from history and update totals.",
      confirmLabel: "Delete record",
      variant: "danger",
      onConfirm: () => executeDeleteCallRecord(call),
    });
  };

  const executeDeleteCustomer = async (customer) => {
    if (!customer?.assignmentId || !isDeveloper) return;
    setDeletingCallId(`customer-${customer.assignmentId}`);
    try {
      await csrOutreachService.deleteCustomerAssignment(customer.assignmentId);
      toast.success("Customer record and related data removed");
      setConfirmModal(null);
      setDataVersion((v) => v + 1);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to delete customer record");
    } finally {
      setDeletingCallId(null);
    }
  };

  const requestDeleteCustomer = (customer) => {
    if (!customer?.assignmentId || !isDeveloper) return;
    const name =
      [customer.firstName, customer.lastName].filter(Boolean).join(" ").trim() || customer.email;
    setConfirmModal({
      title: `Remove "${name}" from CSR outreach?`,
      message: [
        "This deletes all chat messages, call logs, and releases the assignment.",
        "The user returns to the assign pool.",
      ],
      confirmLabel: "Remove customer",
      variant: "danger",
      onConfirm: () => executeDeleteCustomer(customer),
    });
  };

  const saveCsrTargets = async () => {
    if (!targetForm.apply_to_all && !targetForm.agent_id) {
      toast.error("Select a CSR agent");
      return;
    }
    setSavingTargets(true);
    try {
      await csrOutreachService.saveTargets({
        target_date: targetDate,
        agent_id: Number(targetForm.agent_id) || undefined,
        apply_to_all: targetForm.apply_to_all,
        include_self: targetForm.apply_to_all ? targetForm.include_self : undefined,
        target_mode: targetForm.target_mode,
        calls_target: Number(targetForm.calls_target) || 0,
        premium_target:
          targetForm.target_mode === "calls_only" ? 0 : Number(targetForm.premium_target) || 0,
      });
      toast.success(
        targetForm.apply_to_all
          ? `Targets applied to all CSR agents for ${targetDate}`
          : `Targets saved for ${targetDate}`
      );
      notifyDailyTargetsChanged({ team: "csr", targetDate });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save targets");
    } finally {
      setSavingTargets(false);
    }
  };

  const tabs = [
    ...(isCsr ? [{ id: "assign", label: "Assign Users" }] : []),
    ...(isCsr
      ? [
          {
            id: "mine",
            label: "My Users",
            badge: mine.reduce((s, c) => s + (Number(c.unreadCount) || 0), 0),
          },
        ]
      : []),
    ...(isLead || isAdmin ? [{ id: "team", label: "Team View" }] : []),
    ...(isLead || isAdmin ? [{ id: "targets", label: "Daily Targets" }] : []),
    { id: "performance", label: "Performance" },
    ...(isLead || isAdmin ? [{ id: "teams", label: "Teams" }] : []),
  ];

  const openCustomerChat = (c) => {
    setOpenChats((prev) => upsertOpenCsrChat(prev, c));
    if ((Number(c.unreadCount) || 0) > 0) {
      clearUserUnreadLocal("csr", c.userId);
      setMine((prev) =>
        prev.map((row) =>
          Number(row.userId) === Number(c.userId) ? { ...row, unreadCount: 0 } : row
        )
      );
      setTeam((prev) =>
        prev.map((row) =>
          Number(row.userId) === Number(c.userId) ? { ...row, unreadCount: 0 } : row
        )
      );
    }
  };

  const closeCustomerChat = (userId) => {
    setOpenChats((prev) => prev.filter((x) => Number(x.customer?.userId) !== Number(userId)));
  };

  const setChatMinimized = (userId, minimized) => {
    if (!minimized) {
      setOpenChats((prev) => {
        const row = prev.find((x) => Number(x.customer?.userId) === Number(userId));
        return row ? upsertOpenCsrChat(prev, row.customer) : prev;
      });
      clearUserUnreadLocal("csr", userId);
      setMine((prev) =>
        prev.map((row) =>
          Number(row.userId) === Number(userId) ? { ...row, unreadCount: 0 } : row
        )
      );
      return;
    }
    setOpenChats((prev) =>
      prev.map((x) =>
        Number(x.customer?.userId) === Number(userId) ? { ...x, minimized: true } : x
      )
    );
  };

  return (
    <div className="p-4 w-full">
      <div className={`bg-white rounded-xl shadow-xl p-4 md:p-6 min-h-[calc(100vh-120px)] ${openChats.length ? "pb-28" : ""}`}>
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FiUsers style={{ color: THEME.primary }} /> CSR Outreach
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Assign as many users as you need · open several chats at once (minimize extras) · call logged-in users even if they appear offline · track status &amp; premium
            </p>
          </div>
          {quota && isCsr && (
            <div className="text-right text-sm">
              <p className="font-semibold text-gray-800">
                Active: {quota.activeUnresolved ?? 0}
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {tabs.map((t) => (
            <TabBtn
              key={t.id}
              active={tab === t.id}
              onClick={() => setTab(t.id)}
              badge={t.badge}
            >
              {t.label}
            </TabBtn>
          ))}
        </div>

        {isAdmin && (tab === "team" || tab === "mine") && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
            View only — Admin/Developer accounts can monitor CSR outreach but cannot contact customers, log
            call time, change status, or update buy premium.
          </p>
        )}

        {tab === "assign" && isCsr && (
          <div className="mb-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setPhoneLead("");
                setPhoneLookup(null);
                setPhoneLookupError("");
                setShowAddByPhone(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
              style={{ backgroundColor: THEME.primary }}
            >
              <FiPhone /> Add lead by phone
            </button>
          </div>
        )}
        {tab === "assign" && isCsr && (
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              type="button"
              onClick={() => setSelectedIds(new Set(list.map((c) => c.userId)))}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100"
            >
              Clear
            </button>
            {selectedIds.size > 0 && (
              <button
                type="button"
                onClick={handleBulkAssign}
                disabled={assigning}
                className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}
              >
                <FiUserPlus className="inline mr-1" /> Assign selected ({selectedIds.size})
              </button>
            )}
          </div>
        )}

        {tab === "performance" && (
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => setPeriod(period === "daily" ? "monthly" : "daily")}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-gray-100"
            >
              {period === "daily" ? "Show monthly" : "Show daily"}
            </button>
          </div>
        )}

        {tab === "teams" && (
          <p className="text-sm text-gray-500 mb-4">
            Manage team lead &amp; members in{" "}
            <Link to="/support-teams" className="text-red-600 font-semibold underline">
              Support Teams
            </Link>
            . Overview below.
          </p>
        )}

        {(tab === "assign" ||
          tab === "mine" ||
          tab === "team" ||
          tab === "targets" ||
          tab === "performance" ||
          tab === "teams") && (
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <label className="flex flex-col gap-1 min-w-[220px] flex-1 max-w-md">
              <span className="text-[11px] font-semibold text-gray-500">Search</span>
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`${inputCls} w-full`}
              />
            </label>
            {tab === "mine" || tab === "team" ? (
              <>
                <FilterSelect
                  label="Messages"
                  value={filters.unread}
                  onChange={(v) => updateFilter("unread", v)}
                  options={[
                    { value: "", label: "All" },
                    { value: "1", label: "Unread" },
                    { value: "0", label: "Read" },
                  ]}
                />
                <FilterSelect
                  label="Presence"
                  value={filters.online}
                  onChange={(v) => updateFilter("online", v)}
                  options={[
                    { value: "", label: "All" },
                    { value: "1", label: "Online" },
                    { value: "0", label: "Offline" },
                  ]}
                />
                <FilterSelect
                  label="Premium"
                  value={filters.premium}
                  onChange={(v) => updateFilter("premium", v)}
                  options={[
                    { value: "", label: "All" },
                    { value: "1", label: "Premium" },
                    { value: "0", label: "Free" },
                  ]}
                />
                <FilterSelect
                  label="Status"
                  value={filters.stage}
                  onChange={(v) => updateFilter("stage", v)}
                  options={[
                    { value: "", label: "All" },
                    ...stages.map((s) => ({ value: s.stage_key, label: s.name })),
                  ]}
                />
                <FilterSelect
                  label="Buy premium"
                  value={filters.buy_premium}
                  onChange={(v) => updateFilter("buy_premium", v)}
                  options={[
                    { value: "", label: "All" },
                    { value: "1", label: "Yes" },
                    { value: "0", label: "No" },
                  ]}
                />
                <FilterSelect
                  label="Activity"
                  value={filters.messaged}
                  onChange={(v) => updateFilter("messaged", v)}
                  options={[
                    { value: "", label: "All" },
                    { value: "1", label: "Messaged" },
                    { value: "0", label: "Never messaged" },
                  ]}
                />
                {tab === "team" && (
                  <FilterSelect
                    label="CSR agent"
                    value={filters.agent_id}
                    onChange={(v) => updateFilter("agent_id", v)}
                    options={[
                      { value: "", label: "All" },
                      ...teamAgents.map((a) => ({
                        value: String(a.id),
                        label: a.isTeamLeadSelf ? `${a.name || a.email} (You)` : a.name || a.email,
                      })),
                    ]}
                  />
                )}
              </>
            ) : null}
            {tab === "assign" ? (
              <>
                <FilterSelect
                  label="Presence"
                  value={filters.online}
                  onChange={(v) => updateFilter("online", v)}
                  options={[
                    { value: "", label: "All" },
                    { value: "1", label: "Online" },
                    { value: "0", label: "Offline" },
                  ]}
                />
                <FilterSelect
                  label="Premium"
                  value={filters.premium}
                  onChange={(v) => updateFilter("premium", v)}
                  options={[
                    { value: "", label: "All" },
                    { value: "1", label: "Premium" },
                    { value: "0", label: "Free" },
                  ]}
                />
                <FilterSelect
                  label="Account"
                  value={filters.is_active}
                  onChange={(v) => updateFilter("is_active", v)}
                  options={[
                    { value: "", label: "All" },
                    { value: "1", label: "Active" },
                    { value: "0", label: "Disabled" },
                  ]}
                />
              </>
            ) : null}
            {tab === "performance" ? (
              <>
                <FilterSelect
                  label="Activity"
                  value={filters.perfActivity}
                  onChange={(v) => updateFilter("perfActivity", v)}
                  options={[
                    { value: "", label: "All" },
                    { value: "has", label: "Has calls or messages" },
                    { value: "none", label: "No activity" },
                  ]}
                />
                <FilterSelect
                  label="Call source"
                  value={filters.callSource}
                  onChange={(v) => updateFilter("callSource", v)}
                  options={[
                    { value: "", label: "All" },
                    { value: "auto", label: "In-app" },
                    { value: "manual", label: "Manual" },
                  ]}
                />
              </>
            ) : null}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setFilters({ ...EMPTY_FILTERS });
                  setListPage(1);
                }}
                className="px-3 py-2 rounded-lg text-xs font-semibold text-[#FB565F] border border-[#FB565F] bg-white hover:bg-red-50"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {loading ? (
          <TableSkeleton rows={8} cols={6} />
        ) : tab === "targets" ? (
          <div className="space-y-5">
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="font-bold text-gray-900 mb-1">Set CSR daily targets</h2>
              <p className="text-xs text-gray-500 mb-4">
                Targets apply to <strong>CSR agents only</strong> (not Chat Support). Choose calls only, or calls +
                premium. Hitting the target does not stop work — extra calls and users messaged still count. Agents
                do not need a new target to continue.
              </p>
              <div className="flex flex-wrap gap-3 items-end mb-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Date</label>
                  <input
                    type="date"
                    value={targetDate}
                    onChange={(e) => setTargetDate(e.target.value)}
                    className={inputCls}
                  />
                </div>
                <div className="min-w-[200px]">
                  <label className="block text-xs font-semibold text-gray-500 mb-1">CSR agent</label>
                  <select
                    value={targetForm.agent_id}
                    disabled={targetForm.apply_to_all}
                    onChange={(e) => setTargetForm((f) => ({ ...f, agent_id: e.target.value }))}
                    className={`${inputCls} w-full`}
                  >
                    {(targetRows.length ? targetRows : teamAgents).map((a) => (
                      <option key={a.agentId || a.id} value={a.agentId || a.id}>
                        {a.isTeamLeadSelf
                          ? `${a.agentName || a.name || a.email} (You)`
                          : a.agentName || a.name || a.email}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 pb-2">
                  <input
                    type="checkbox"
                    checked={targetForm.apply_to_all}
                    onChange={(e) => setTargetForm((f) => ({ ...f, apply_to_all: e.target.checked }))}
                  />
                  Apply to all CSR agents
                </label>
                {targetForm.apply_to_all ? (
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 pb-2">
                    <input
                      type="checkbox"
                      checked={targetForm.include_self}
                      onChange={(e) => setTargetForm((f) => ({ ...f, include_self: e.target.checked }))}
                    />
                    Include my own target
                  </label>
                ) : null}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Target type</label>
                  <select
                    value={targetForm.target_mode}
                    onChange={(e) => setTargetForm((f) => ({ ...f, target_mode: e.target.value }))}
                    className={`${inputCls} w-full`}
                  >
                    <option value="calls_and_premium">Calls/messages + premium</option>
                    <option value="calls_only">Calls/messages only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Calls / messages target</label>
                  <input
                    type="number"
                    min={0}
                    value={targetForm.calls_target}
                    onChange={(e) => setTargetForm((f) => ({ ...f, calls_target: e.target.value }))}
                    className={`${inputCls} w-full`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Premium target</label>
                  <input
                    type="number"
                    min={0}
                    disabled={targetForm.target_mode === "calls_only"}
                    value={targetForm.premium_target}
                    onChange={(e) => setTargetForm((f) => ({ ...f, premium_target: e.target.value }))}
                    className={`${inputCls} w-full ${
                      targetForm.target_mode === "calls_only" ? "bg-gray-50 text-gray-400" : ""
                    }`}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={saveCsrTargets}
                disabled={savingTargets || !(targetRows.length || teamAgents.length)}
                className="px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}
              >
                {savingTargets ? "Saving…" : "Save targets"}
              </button>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">CSR</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">Mode</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Calls / msgs target</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Calls / msgs done</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Premium target</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Premium done</th>
                  </tr>
                </thead>
                <tbody>
                  {targetRowsPagination.pageItems.map((r) => (
                    <tr key={r.agentId} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {r.isTeamLeadSelf
                          ? `${r.agentName || r.agentEmail} (You)`
                          : r.agentName || r.agentEmail}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {r.targets?.targetMode === "calls_only"
                          ? "Calls / messages only"
                          : r.targetsSet
                            ? "Calls / messages + premium"
                            : "—"}
                      </td>
                      <td className="px-3 py-2 text-center">{r.targets?.callsTarget ?? "—"}</td>
                      <td className="px-3 py-2 text-center">{r.actuals?.callsCount ?? 0}</td>
                      <td className="px-3 py-2 text-center">
                        {r.targets?.targetMode === "calls_only"
                          ? "—"
                          : r.targets?.premiumTarget ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-center">{r.actuals?.premiumCount ?? 0}</td>
                    </tr>
                  ))}
                  {!filteredTargetRows.length && (
                    <tr>
                      <td colSpan={6} className="px-3 py-12 text-center text-gray-400">
                        {targetRows.length
                          ? "No agents match these filters"
                          : "No CSR agents found. Assign CSR users to a team first."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <PaginationBar
              page={targetRowsPagination.page}
              totalPages={targetRowsPagination.totalPages}
              totalItems={targetRowsPagination.totalItems}
              pageSize={LIST_PAGE_SIZE}
              label="agents"
              onPageChange={targetRowsPagination.setPage}
            />
          </div>
        ) : tab === "performance" ? (
          <div className="space-y-5">
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">CSR</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Active</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Registered</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Lead premium</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Buy premium</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Calls</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Users messaged</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Call min</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Logged min</th>
                  </tr>
                </thead>
                <tbody>
                  {performancePagination.pageItems.map((r) => (
                    <tr key={r.agentId} className="border-t">
                      <td className="px-3 py-2">
                        <p className="font-medium">{r.name}</p>
                        <p className="text-xs text-gray-400">{r.email}</p>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {r.activeUnresolved ?? 0}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => openAttributedClients(r)}
                          className="font-semibold text-red-600 hover:underline disabled:text-gray-400 disabled:no-underline"
                          title="View clients registered through this agent"
                          disabled={!Number(r.preRegRegisteredCount)}
                        >
                          {r.preRegRegisteredCount ?? 0}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-center">{r.preRegPremiumCount ?? 0}</td>
                      <td className="px-3 py-2 text-center">{r.buyPremiumCount ?? 0}</td>
                      <td className="px-3 py-2 text-center">{r.calls_count || 0}</td>
                      <td className="px-3 py-2 text-center">{r.messages_count || 0}</td>
                      <td className="px-3 py-2 text-center">
                        {Math.round((r.calls_duration_sec || 0) / 60)}
                      </td>
                      <td className="px-3 py-2 text-center">{Math.round(r.totalCallMinutes || 0)}</td>
                    </tr>
                  ))}
                  {!filteredPerformance.length && (
                    <tr>
                      <td colSpan={9} className="px-3 py-12 text-center text-gray-400">
                        {performance.length
                          ? "No agents match these filters"
                          : "No performance data yet"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <PaginationBar
              page={performancePagination.page}
              totalPages={performancePagination.totalPages}
              totalItems={performancePagination.totalItems}
              pageSize={LIST_PAGE_SIZE}
              label="agents"
              onPageChange={performancePagination.setPage}
            />

            <section className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
              <h3 className="text-sm font-bold text-gray-800 mb-1">Call records</h3>
              <p className="text-xs text-gray-500 mb-3">
                Every manual log and in-app chat call for this period ({period === "daily" ? "today" : "this month"}).
              </p>
              <CallRecordsTable
                records={callRecordsPagination.pageItems}
                showAgent={isLead || isAdmin}
                canDelete={isDeveloper}
                deletingId={deletingCallId}
                onDelete={requestDeleteCallRecord}
                emptyMessage="No call records for this period yet."
              />
              <PaginationBar
                page={callRecordsPagination.page}
                totalPages={callRecordsPagination.totalPages}
                totalItems={callRecordsPagination.totalItems}
                pageSize={LIST_PAGE_SIZE}
                label="call records"
                onPageChange={callRecordsPagination.setPage}
              />
            </section>
          </div>
        ) : tab === "teams" ? (
          <div className="space-y-4">
            {filteredTeams.map((t) => (
              <div key={t.id} className="rounded-xl border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900">{t.name}</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Lead: {t.teamLeadName || "—"} · {t.members?.length || 0} members
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(t.members || []).map((m) => (
                    <span
                      key={m.id}
                      className="text-xs px-2 py-1 rounded-lg bg-gray-50 border text-gray-700"
                    >
                      {m.firstName} {m.lastName}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            {!filteredTeams.length && (
              <p className="text-center text-gray-400 py-12">
                {teams.length
                  ? "No teams match this search"
                  : "No teams yet — create one in Support Teams"}
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {tab === "assign" && <th className="px-3 py-2 w-10" />}
                  <th className="px-3 py-2 text-left font-semibold text-gray-500">Customer</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500">Session</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500">Premium</th>
                  {tab !== "assign" && (
                    <>
                      <th className="px-3 py-2 text-left font-semibold text-gray-500">Status</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-500">Buy premium</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-500">Call min</th>
                    </>
                  )}
                  {tab === "team" && (
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">CSR</th>
                  )}
                  <th className="px-3 py-2 text-right font-semibold text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.userId || c.assignmentId} className="border-t">
                    {tab === "assign" && (
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(c.userId)}
                          onChange={() => toggle(c.userId)}
                        />
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="min-w-0">
                          <p className="font-medium">
                            {c.firstName} {c.lastName}
                          </p>
                          <p className="text-xs text-gray-400">{c.email}</p>
                        </div>
                        {(tab === "mine" || tab === "team") && <UnreadBadge count={c.unreadCount} />}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <SessionCell
                        customer={c}
                        showOtherCsrHint={tab === "team" && isCsr}
                        myAgentId={myAgentId}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                          c.isPremium ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {c.isPremium ? "Premium" : "Free"}
                      </span>
                    </td>
                    {tab !== "assign" && (
                      <>
                        <td className="px-3 py-2">
                          {canManageCsrCustomers ? (
                            <select
                              value={stages.find((s) => s.stage_key === c.stageKey)?.id || ""}
                              onChange={(e) => handleStage(c.assignmentId, Number(e.target.value))}
                              className="border border-gray-200 rounded-lg px-2 py-1 text-xs"
                            >
                              {stages.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-xs font-semibold text-gray-600">
                              {c.stageName || c.stageKey || "—"}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {canManageCsrCustomers ? (
                            <div className="flex flex-col items-start gap-1">
                              {c.isPremium ? (
                                <span className="text-[11px] font-semibold text-emerald-700">
                                  Already premium — not required
                                </span>
                              ) : (
                                <>
                                  <label className="inline-flex items-center gap-1 text-xs">
                                    <input
                                      type="checkbox"
                                      checked={
                                        !!c.buyPremium || c.premiumRequest?.status === "pending"
                                      }
                                      disabled={
                                        !!c.buyPremium || c.premiumRequest?.status === "pending"
                                      }
                                      onChange={(e) => handlePremium(c, e.target.checked)}
                                    />
                                    Buy premium
                                  </label>
                                  {premiumStatusBadge(c)}
                                  {c.premiumRequest?.status === "rejected" && (
                                    <button
                                      type="button"
                                      className="text-[10px] font-semibold text-blue-700 hover:underline"
                                      onClick={() => setPremiumModalCustomer(c)}
                                    >
                                      Resubmit proof
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1">
                              <span className="text-xs text-gray-600">
                                {c.isPremium
                                  ? "Already premium"
                                  : c.buyPremium
                                    ? "Yes"
                                    : "No"}
                              </span>
                              {!c.isPremium && premiumStatusBadge(c)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => setHistoryModal(c)}
                            className="text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline"
                            title="View all call records"
                          >
                            {Number(c.callMinutes || 0).toFixed(1)} min
                          </button>
                        </td>
                      </>
                    )}
                    {tab === "team" && (
                      <td className="px-3 py-2 text-xs text-gray-600">{c.agentName || "—"}</td>
                    )}
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-wrap justify-end gap-1">
                        {tab === "assign" ? (
                          <button
                            type="button"
                            disabled={assigning}
                            onClick={async () => {
                              setAssigning(true);
                              try {
                                await csrOutreachService.assign([c.userId]);
                                toast.success("Assigned");
                                refresh();
                              } catch (e) {
                                toast.error(e?.response?.data?.message || "Failed");
                              } finally {
                                setAssigning(false);
                              }
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                            style={{ backgroundColor: THEME.primary }}
                          >
                            <FiUserPlus /> Assign
                          </button>
                        ) : canManageCsrCustomers ? (
                          <>
                            <button
                              type="button"
                              onClick={() => openCustomerChat(c)}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold ${
                                openChatUserIds.has(Number(c.userId))
                                  ? "bg-emerald-600 text-white"
                                  : "bg-emerald-50 text-emerald-700"
                              }`}
                            >
                              <FiMessageCircle />{" "}
                              {openChatUserIds.has(Number(c.userId)) ? "Chatting" : "Contact"}
                            </button>
                            <button
                              type="button"
                              onClick={() => openLogCallModal(c)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700"
                            >
                              <FiClock /> Log time
                            </button>
                            {(isLead || isAdmin) && tab === "team" && c.assignmentId && (
                              <>
                                <select
                                  className="border border-gray-200 rounded-lg px-1.5 py-1 text-[11px] max-w-[130px] disabled:opacity-50"
                                  defaultValue=""
                                  disabled={reassigningId === c.assignmentId || unassigningId === c.assignmentId}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    e.target.value = "";
                                    if (v) handleReassign(c.assignmentId, v);
                                  }}
                                >
                                  <option value="">
                                    {reassigningId === c.assignmentId ? "Shifting…" : "Shift to…"}
                                  </option>
                                  {teamAgents
                                    .filter((a) => Number(a.id) !== Number(c.agentId))
                                    .map((a) => (
                                      <option key={a.id} value={a.id}>
                                        {a.name}
                                      </option>
                                    ))}
                                </select>
                                <button
                                  type="button"
                                  onClick={() => handleRelease(c.assignmentId)}
                                  disabled={reassigningId === c.assignmentId || unassigningId === c.assignmentId}
                                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-semibold bg-amber-50 text-amber-800 disabled:opacity-50"
                                >
                                  {unassigningId === c.assignmentId ? (
                                    <>
                                      <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-amber-700 border-t-transparent" />
                                      Unassigning…
                                    </>
                                  ) : (
                                    "Unassign"
                                  )}
                                </button>
                                {reassigningId === c.assignmentId && (
                                  <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
                                    <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
                                    Shifting…
                                  </span>
                                )}
                              </>
                            )}
                            {isDeveloper && (
                              <button
                                type="button"
                                onClick={() => requestDeleteCustomer(c)}
                                disabled={deletingCallId === `customer-${c.assignmentId}`}
                                className="inline-flex items-center justify-center p-2 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 disabled:opacity-40"
                                title="Delete customer & all related data (Developer only)"
                              >
                                <FiTrash2 size={14} />
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-gray-400 italic">View only</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!list.length && (
                  <tr>
                    <td colSpan={8} className="px-3 py-12 text-center text-gray-400">
                      No users in this view
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && ["assign", "mine", "team"].includes(tab) && (
          <PaginationBar
            page={listPage}
            totalPages={listTotalPages}
            totalItems={listTotal}
            pageSize={LIST_PAGE_SIZE}
            label="customers"
            onPageChange={setListPage}
          />
        )}

        {isAdmin && tab === "performance" && (
          <p className="text-xs text-gray-400 mt-4 flex items-center gap-1">
            <FiTrendingUp /> Admin overview — all CSR agents. Chat Support agents work in Support Center.
          </p>
        )}
      </div>

      {layoutCsrChatDock(openChats).map((item) => (
        <CsrCustomerChat
          key={item.customer.userId}
          customer={item.customer}
          minimized={item.minimized}
          offsetRight={item.offsetRight}
          dockZIndex={item.dockZIndex}
          unreadCount={
            [...mine, ...team].find(
              (row) => Number(row.userId) === Number(item.customer.userId)
            )?.unreadCount || 0
          }
          onMinimizedChange={(next) => setChatMinimized(item.customer.userId, next)}
          onClose={() => closeCustomerChat(item.customer.userId)}
          onCallEnded={refresh}
        />
      ))}

      {historyModal && (
        <CsrCallHistoryModal
          customer={historyModal}
          onClose={() => setHistoryModal(null)}
          onUpdated={refresh}
          canDelete={isDeveloper}
          onDelete={requestDeleteCallRecord}
          deletingId={deletingCallId}
          dataVersion={dataVersion}
        />
      )}

      <ConfirmWarningModal
        open={Boolean(confirmModal)}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmLabel={confirmModal?.confirmLabel}
        variant={confirmModal?.variant || "danger"}
        confirming={Boolean(deletingCallId)}
        onClose={() => {
          if (!deletingCallId) setConfirmModal(null);
        }}
        onConfirm={() => confirmModal?.onConfirm?.()}
      />

      {premiumModalCustomer && (
        <CsrPremiumProofModal
          customer={premiumModalCustomer}
          onClose={() => setPremiumModalCustomer(null)}
          onSubmitted={refresh}
        />
      )}

      {approvalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5">
            <h3 className="text-lg font-bold text-gray-900">
              {approvalModal.status === "approved" ? "Accept payment proof?" : "Reject payment proof?"}
            </h3>
            <p className="text-sm text-gray-500 mt-1 mb-4">
              {approvalModal.status === "approved"
                ? `Approve proof for ${approvalModal.label} and grant premium.`
                : `Reject proof for ${approvalModal.label}. CSR can resubmit.`}
            </p>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Notes (optional)</label>
            <textarea
              value={approvalModal.notes}
              onChange={(e) => setApprovalModal((m) => ({ ...m, notes: e.target.value }))}
              className={`${inputCls} w-full min-h-[70px] mb-4`}
              placeholder="Reason or verification notes…"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setApprovalModal(null)}
                disabled={!!approvingId}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePremiumApproval}
                disabled={!!approvingId}
                className={`px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 ${
                  approvalModal.status === "approved" ? "bg-emerald-600" : "bg-rose-600"
                }`}
              >
                {approvingId
                  ? "Saving…"
                  : approvalModal.status === "approved"
                    ? "Accept & grant premium"
                    : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {callModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
            <h3 className="font-bold text-gray-900 mb-1">Log call record</h3>
            <p className="text-xs text-gray-500 mb-4">
              Add a manual call entry. Each save creates a separate row in call history.
            </p>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Client email</label>
            <input
              type="email"
              readOnly
              value={callModal.email}
              className={`${inputCls} w-full mb-3 bg-gray-50`}
            />
            <label className="block text-xs font-semibold text-gray-500 mb-1">Call date & time</label>
            <input
              type="datetime-local"
              value={callModal.started_at}
              onChange={(e) => setCallModal((m) => ({ ...m, started_at: e.target.value }))}
              className={`${inputCls} w-full mb-3`}
            />
            <label className="block text-xs font-semibold text-gray-500 mb-1">Duration</label>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div>
                <span className="text-[10px] text-gray-400">Hours</span>
                <input
                  type="number"
                  min={0}
                  value={callModal.hours}
                  onChange={(e) => setCallModal((m) => ({ ...m, hours: e.target.value }))}
                  className={`${inputCls} w-full`}
                />
              </div>
              <div>
                <span className="text-[10px] text-gray-400">Minutes</span>
                <input
                  type="number"
                  min={0}
                  value={callModal.minutes}
                  onChange={(e) => setCallModal((m) => ({ ...m, minutes: e.target.value }))}
                  className={`${inputCls} w-full`}
                />
              </div>
              <div>
                <span className="text-[10px] text-gray-400">Seconds</span>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={callModal.seconds}
                  onChange={(e) => setCallModal((m) => ({ ...m, seconds: e.target.value }))}
                  className={`${inputCls} w-full`}
                />
              </div>
            </div>
            <textarea
              placeholder="Notes (optional)"
              value={callModal.notes}
              onChange={(e) => setCallModal((m) => ({ ...m, notes: e.target.value }))}
              className={`${inputCls} w-full mb-4 min-h-[60px]`}
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setCallModal(null)}
                disabled={loggingCall}
                className="px-4 py-2 text-sm rounded-lg border"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLogMinutes}
                disabled={loggingCall}
                className="px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}
              >
                {loggingCall ? "Saving…" : "Save record"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddByPhone && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <FiPhone style={{ color: THEME.primary }} /> Add lead by phone
              </h3>
              <button
                type="button"
                onClick={() => setShowAddByPhone(false)}
                className="text-gray-400 hover:text-gray-700"
              >
                <FiXCircle />
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Phone is required. If the user exists and is already assigned to another CSR, you cannot claim them — only a Team Lead can unassign or shift.
            </p>
            <input
              className={inputCls + " w-full"}
              placeholder="Phone number"
              value={phoneLead}
              onChange={(e) => setPhoneLead(e.target.value)}
            />
            {phoneChecking ? (
              <p className="text-[11px] text-gray-400">Checking phone…</p>
            ) : phoneLookupError ? (
              <p className="text-[11px] text-rose-600">{phoneLookupError}</p>
            ) : phoneLookup?.userExists ? (
              <p className="text-[11px] text-emerald-700">
                User found: {phoneLookup.matchedUser?.name || phoneLookup.matchedUser?.email}
                {phoneLookup.matchedUser?.contactNo ? ` · ${phoneLookup.matchedUser.contactNo}` : ""}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowAddByPhone(false)}
                className="px-4 py-2 rounded-lg bg-gray-100 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={phoneAssigning || phoneChecking || Boolean(phoneLookupError) || !phoneLead.trim()}
                onClick={handleAssignByPhone}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}
              >
                {phoneAssigning ? "Assigning…" : "Assign to me"}
              </button>
            </div>
          </div>
        </div>
      )}

      {attributedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Registered via CSR Leads
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {attributedModal.agentName}
                  {attributedModal.from
                    ? ` · ${attributedModal.from}${
                        attributedModal.to && attributedModal.to !== attributedModal.from
                          ? ` → ${attributedModal.to}`
                          : ""
                      }`
                    : ""}
                  {` · ${attributedTotal} client${attributedTotal === 1 ? "" : "s"}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setAttributedModal(null);
                  setAttributedUsers([]);
                }}
                className="p-1 rounded hover:bg-gray-100"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-4">
              {attributedLoading ? (
                <TableSkeleton rows={6} cols={5} />
              ) : attributedUsers.length === 0 ? (
                <p className="text-center text-gray-400 py-10 text-sm">
                  No clients registered through this agent in this period.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2 font-semibold">User</th>
                        <th className="px-3 py-2 font-semibold">Email</th>
                        <th className="px-3 py-2 font-semibold">Phone</th>
                        <th className="px-3 py-2 font-semibold">Status</th>
                        <th className="px-3 py-2 font-semibold">Registered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attributedUsers.map((u) => (
                        <tr key={u.id || u.userId} className="border-t">
                          <td className="px-3 py-2">
                            <p className="font-medium text-gray-900">{u.userName || u.fullName}</p>
                            {u.userId ? (
                              <p className="text-[11px] text-gray-400">#{u.userId}</p>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-gray-700">{u.userEmail || "—"}</td>
                          <td className="px-3 py-2 text-gray-700">{u.userPhone || "—"}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${
                                u.status === "premium"
                                  ? "bg-indigo-50 text-indigo-800 border-indigo-200"
                                  : "bg-emerald-50 text-emerald-800 border-emerald-200"
                              }`}
                            >
                              {u.status === "premium" ? "Premium" : "Registered"}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                            {u.registeredAt
                              ? new Date(u.registeredAt).toLocaleString()
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t flex justify-between items-center gap-2">
              <Link
                to="/csr-leads"
                className="text-xs font-semibold text-red-600 hover:underline"
              >
                Open CSR Leads
              </Link>
              <button
                type="button"
                onClick={() => {
                  setAttributedModal(null);
                  setAttributedUsers([]);
                }}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
